import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Collapse,
  useTheme,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  SwapHoriz as SwapIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  RestaurantMenu as ChefIcon,
  Warning as WarningIcon,
  CheckCircle as ConfidenceIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import ingredientSubstitutionService, { 
  IngredientSubstitutionData, 
  SubstitutionRequest,
  RecipeRewriteRequest 
} from '../services/ingredientSubstitutionService';
import { Recipe } from '../models/Recipe';

interface IngredientSubstitutionProps {
  ingredients: string[];
  recipeTitle?: string;
  cuisine?: string;
  recipe?: Recipe;
  onRecipeUpdate?: (updatedRecipe: Recipe, changes: string[]) => void;
  onSubstitutionSelect?: (original: string, substitute: string) => void;
}

const IngredientSubstitution: React.FC<IngredientSubstitutionProps> = ({
  ingredients,
  recipeTitle,
  cuisine,
  recipe,
  onRecipeUpdate,
  onSubstitutionSelect,
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState('');
  const [substitutionReason, setSubstitutionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [substitutions, setSubstitutions] = useState<IngredientSubstitutionData[]>([]);
  const [generalAdvice, setGeneralAdvice] = useState('');
  const [bulkSubstitutions, setBulkSubstitutions] = useState<Map<string, IngredientSubstitutionData[]>>(new Map());
  const [loadingBulk, setLoadingBulk] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSubstitution, setPendingSubstitution] = useState<{
    original: string;
    substitute: string;
    ratio: string;
    notes: string;
  } | null>(null);
  const [applyingSubstitution, setApplyingSubstitution] = useState(false);

  const parsedIngredients = ingredients.length > 0 
    ? ingredients 
    : ingredientSubstitutionService.parseIngredientsList(ingredients.join('\n'));

  const handleGetSubstitution = async () => {
    if (!selectedIngredient) {
      return;
    }

    setLoading(true);
    try {
      const request: SubstitutionRequest = {
        ingredient: selectedIngredient,
        reason: substitutionReason || undefined,
        cuisine: cuisine,
        recipeType: 'general cooking',
      };

      const response = await ingredientSubstitutionService.getSubstitutions(request);
      setSubstitutions(response.substitutions);
      setGeneralAdvice(response.generalAdvice || '');
    } catch (error) {
      console.error('Error getting substitutions:', error);
      setSubstitutions([{
        original: selectedIngredient,
        substitute: 'Error loading substitutions',
        ratio: '1:1',
        notes: 'Please try again or consult a cooking reference.',
        confidence: 'low'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleGetBulkSubstitutions = async () => {
    if (parsedIngredients.length === 0) {
      return;
    }

    setLoadingBulk(true);
    try {
      const context = {
        recipeTitle: recipeTitle,
        cuisine: cuisine,
      };

      const response = await ingredientSubstitutionService.getBulkSubstitutions(parsedIngredients, context);
      setBulkSubstitutions(response);
    } catch (error) {
      console.error('Error getting bulk substitutions:', error);
      // Set error state for bulk substitutions
      const errorMap = new Map();
      parsedIngredients.forEach(ingredient => {
        errorMap.set(ingredient, [{
          original: ingredient,
          substitute: 'Unable to load substitutions',
          ratio: '1:1',
          notes: 'Service temporarily unavailable',
          confidence: 'low' as const
        }]);
      });
      setBulkSubstitutions(errorMap);
    } finally {
      setLoadingBulk(false);
    }
  };

  const handleSubstituteClick = (original: string, substitute: string, ratio?: string, notes?: string) => {
    // Show confirmation dialog if recipe updating is enabled
    if (recipe && onRecipeUpdate) {
      setPendingSubstitution({
        original,
        substitute,
        ratio: ratio || '1:1',
        notes: notes || ''
      });
      setConfirmDialogOpen(true);
    } else if (onSubstitutionSelect) {
      onSubstitutionSelect(original, substitute);
    }
  };

  const handleConfirmSubstitution = async () => {
    if (!pendingSubstitution || !recipe || !onRecipeUpdate) {
      return;
    }

    setApplyingSubstitution(true);
    try {
      const request: RecipeRewriteRequest = {
        originalIngredients: recipe.ingredients,
        substitution: pendingSubstitution,
        recipeTitle: recipe.title,
        recipeSteps: recipe.steps?.map(step => step.content)
      };

      const response = await ingredientSubstitutionService.rewriteRecipeWithSubstitution(request);
      
      // Create updated recipe object
      const updatedRecipe: Recipe = {
        ...recipe,
        ingredients: response.updatedIngredients,
        steps: response.updatedSteps ? response.updatedSteps.map((content, index) => ({
          id: recipe.steps?.[index]?.id || `step-${index}`,
          content,
          image: recipe.steps?.[index]?.image,
          audio: recipe.steps?.[index]?.audio
        })) : recipe.steps
      };

      onRecipeUpdate(updatedRecipe, response.changes);
      
      setConfirmDialogOpen(false);
      setPendingSubstitution(null);
    } catch (error) {
      console.error('Error applying substitution:', error);
    } finally {
      setApplyingSubstitution(false);
    }
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return theme.palette.success.main;
      case 'medium':
        return theme.palette.warning.main;
      default:
        return theme.palette.error.main;
    }
  };

  const getConfidenceIcon = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return <ConfidenceIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'medium':
        return <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      default:
        return <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    }
  };

  if (parsedIngredients.length === 0) {
    return null;
  }

  return (
    <Box>
      {/* Expandable Section Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          cursor: 'pointer',
          p: 1,
          borderRadius: 2,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 138, 101, 0.05)'
              : 'rgba(255, 107, 53, 0.05)',
          },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChefIcon 
            sx={{ 
              color: 'primary.main', 
              fontSize: 20 
            }} 
          />
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              fontFamily: '"Playfair Display", Georgia, serif',
            }}
          >
            Ingredient Substitutions
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: 'primary.main' }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Expandable Content */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2, pl: 1 }}>
          {/* Quick Actions */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SearchIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              Find Substitute
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={loadingBulk ? <CircularProgress size={16} /> : <SwapIcon />}
              onClick={handleGetBulkSubstitutions}
              disabled={loadingBulk}
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              {loadingBulk ? 'Loading...' : 'Show All Options'}
            </Button>
          </Box>

          {/* Bulk Substitutions Display */}
          <AnimatePresence>
            {bulkSubstitutions.size > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ mb: 2 }}>
                  {Array.from(bulkSubstitutions.entries()).map(([ingredient, subs]) => (
                    <Card 
                      key={ingredient} 
                      elevation={0}
                      sx={{ 
                        mb: 1.5,
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.02)'
                          : 'rgba(255, 248, 240, 0.3)',
                        border: 1,
                        borderColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 138, 101, 0.1)'
                          : 'rgba(255, 107, 53, 0.15)',
                        borderRadius: 2,
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 600, 
                            mb: 1,
                            color: 'primary.main',
                            fontFamily: '"Playfair Display", Georgia, serif',
                          }}
                        >
                          {ingredient}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {subs.slice(0, 3).map((sub, index) => (
                            <Chip
                              key={index}
                              label={`${sub.substitute} (${sub.ratio})`}
                              size="small"
                              icon={getConfidenceIcon(sub.confidence)}
                              onClick={() => handleSubstituteClick(ingredient, sub.substitute, sub.ratio, sub.notes)}
                              clickable
                              sx={{
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? 'rgba(255, 138, 101, 0.1)'
                                  : 'rgba(255, 107, 53, 0.1)',
                                borderColor: getConfidenceColor(sub.confidence),
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 138, 101, 0.2)'
                                    : 'rgba(255, 107, 53, 0.2)',
                                },
                                fontFamily: '"Playfair Display", Georgia, serif',
                                fontSize: '0.75rem',
                              }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Collapse>

      {/* Individual Substitution Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapIcon sx={{ color: 'primary.main' }} />
            <Typography 
              variant="h6"
              sx={{ fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              Find Ingredient Substitute
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select Ingredient</InputLabel>
              <Select
                value={selectedIngredient}
                onChange={(e) => setSelectedIngredient(e.target.value)}
                label="Select Ingredient"
              >
                {parsedIngredients.map((ingredient, index) => (
                  <MenuItem key={index} value={ingredient}>
                    {ingredient}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Reason for Substitution (Optional)"
              placeholder="e.g., allergic to nuts, don't have this ingredient, want healthier option"
              value={substitutionReason}
              onChange={(e) => setSubstitutionReason(e.target.value)}
            />

            {substitutions.length > 0 && (
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2,
                    fontFamily: '"Playfair Display", Georgia, serif',
                    color: 'primary.main',
                  }}
                >
                  Substitution Options
                </Typography>
                <List>
                  {substitutions.map((sub, index) => (
                    <ListItem 
                      key={index}
                      sx={{
                        border: 1,
                        borderColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 138, 101, 0.2)'
                          : 'rgba(255, 107, 53, 0.2)',
                        borderRadius: 2,
                        mb: 1,
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.02)'
                          : 'rgba(255, 248, 240, 0.3)',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {sub.substitute}
                            </Typography>
                            <Chip
                              size="small"
                              label={sub.ratio}
                              color="primary"
                              variant="outlined"
                              sx={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                            />
                            {getConfidenceIcon(sub.confidence)}
                          </Box>
                        }
                        secondary={
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.secondary',
                              fontStyle: 'italic',
                            }}
                          >
                            {sub.notes}
                          </Typography>
                        }
                      />
                      <Button
                        size="small"
                        onClick={() => handleSubstituteClick(sub.original, sub.substitute, sub.ratio, sub.notes)}
                        sx={{ 
                          minWidth: 'auto',
                          fontFamily: '"Playfair Display", Georgia, serif',
                        }}
                      >
                        Use This
                      </Button>
                    </ListItem>
                  ))}
                </List>

                {generalAdvice && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box 
                      sx={{ 
                        p: 2, 
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(33, 150, 243, 0.1)'
                          : 'rgba(25, 118, 210, 0.1)',
                        borderRadius: 2,
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontStyle: 'italic',
                          color: 'text.secondary',
                        }}
                      >
                        <strong>Chef's Tip:</strong> {generalAdvice}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          <Button 
            onClick={handleGetSubstitution}
            variant="contained"
            disabled={!selectedIngredient || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
          >
            {loading ? 'Finding...' : 'Get Substitutions'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Substitution Confirmation Dialog */}
      <Dialog 
        open={confirmDialogOpen} 
        onClose={() => setConfirmDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapIcon sx={{ color: 'primary.main' }} />
            <Typography 
              variant="h6"
              sx={{ fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              Confirm Substitution
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {pendingSubstitution && (
            <Box sx={{ py: 1 }}>
              <Typography variant="body1" paragraph>
                Replace <strong>{pendingSubstitution.original}</strong> with <strong>{pendingSubstitution.substitute}</strong>?
              </Typography>
              
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 138, 101, 0.1)'
                    : 'rgba(255, 107, 53, 0.1)',
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Substitution Details:
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Ratio:</strong> {pendingSubstitution.ratio}
                </Typography>
                {pendingSubstitution.notes && (
                  <Typography variant="body2">
                    <strong>Notes:</strong> {pendingSubstitution.notes}
                  </Typography>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary">
                This will update your recipe ingredients and cooking steps if needed. The changes will be applied using AI to maintain recipe integrity.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSubstitution}
            variant="contained"
            disabled={applyingSubstitution}
            startIcon={applyingSubstitution ? <CircularProgress size={16} /> : <CheckIcon />}
            sx={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            {applyingSubstitution ? 'Updating Recipe...' : 'Confirm Substitution'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IngredientSubstitution;